import React from 'react';

import FileUploadProgress from '@/components/molecules/FileUploadProgress/FileUploadProgress';
import SixDetailFileBrowserControls from '@/components/organism/SixDetail/SixDetailFileBrowser/SixDetailFileBrowserControls';
import SixDetailFileBrowserDesktopRow from '@/components/organism/SixDetail/SixDetailFileBrowser/SixDetailFileBrowserDesktopRow';
import SixDetailFileBrowserMobileRow from '@/components/organism/SixDetail/SixDetailFileBrowser/SixDetailFileBrowserMobileRow';
import SixDetailFileBrowserTableHeader from '@/components/organism/SixDetail/SixDetailFileBrowser/SixDetailFileBrowserTableHeader';

import type { SixDetailFileBrowserProps } from './SixDetailFileBrowser.types';
import { getNextSortDir } from './fileBrowserRow.utils';

function SixDetailFileBrowser({
  items,
  showPathColumn,
  uploads,
  showUploads,
  searchQuery,
  downloadFilterKey,
  onSearchChange,
  onDownloadFilterChange,
  sortKey,
  sortDir,
  onSortChange,
  selectedIds,
  onSelectionChange,
  onItemAction,
  canShowRowActions,
  onUploadProgressComplete,
  isDragDisabled = true,
  searchPlaceholder = 'Search files...',
}: SixDetailFileBrowserProps): React.ReactElement {
  const isPathColumnVisible = showPathColumn;
  const safeDownloadFilterKey =
    downloadFilterKey === 'downloaded' || downloadFilterKey === 'notDownloaded'
      ? downloadFilterKey
      : 'allFiles';

  const handleDownloadFilterChange = (nextFilterKey: 'allFiles' | 'downloaded' | 'notDownloaded'): void => {
    onDownloadFilterChange(nextFilterKey);
  };

  function handleToggleSelect(id: string): void {
    const nextSelectedIds = selectedIds.includes(id)
      ? selectedIds.filter((selectedId) => selectedId !== id)
      : [...selectedIds, id];

    onSelectionChange(nextSelectedIds);
  }

  function handleSort(column: typeof sortKey): void {
    onSortChange(column, getNextSortDir(sortKey, sortDir, column));
  }

  return (
    <div className="w-full">
      {showUploads && uploads.length > 0 && (
        <div className="mb-4 space-y-3">
          {uploads.map((upload) => (
            <FileUploadProgress
              key={upload.uploadId}
              uploadId={upload.uploadId}
              fileName={upload.fileName}
              progress={upload.progress}
              stage={upload.stage}
              error={upload.error}
              retryCount={upload.retryCount}
              onComplete={onUploadProgressComplete}
            />
          ))}
        </div>
      )}

      <SixDetailFileBrowserControls
        searchQuery={searchQuery}
        downloadFilterKey={safeDownloadFilterKey}
        onSearchChange={onSearchChange}
        onDownloadFilterChange={handleDownloadFilterChange}
        sortKey={sortKey}
        sortDir={sortDir}
        onSortChange={onSortChange}
        searchPlaceholder={searchPlaceholder}
      />

      <div className="my-4 hidden border-t border-muted pt-4 md:block">
        <div className="overflow-hidden rounded-lg border border-border">
          <SixDetailFileBrowserTableHeader
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={handleSort}
            showPathColumn={isPathColumnVisible}
          />

          {items.map((item) => (
            <SixDetailFileBrowserDesktopRow
              key={item.id}
              item={item}
              showPathColumn={isPathColumnVisible}
              isSelected={selectedIds.includes(item.id)}
              canShowRowActions={canShowRowActions}
              onItemAction={onItemAction}
              onToggleSelect={handleToggleSelect}
              selectedIds={selectedIds}
              isDragDisabled={isDragDisabled}
            />
          ))}
        </div>
      </div>

      <div className="space-y-3 border-t border-muted p-4 md:hidden">
        {items.map((item) => (
          <SixDetailFileBrowserMobileRow
            key={item.id}
            item={item}
            showPathColumn={isPathColumnVisible}
            isSelected={selectedIds.includes(item.id)}
            canShowRowActions={canShowRowActions}
            onItemAction={onItemAction}
            onToggleSelect={handleToggleSelect}
            selectedIds={selectedIds}
            isDragDisabled={isDragDisabled}
          />
        ))}
      </div>
    </div>
  );
}

export default SixDetailFileBrowser;
